// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What each immersive mode asks the runtime for, and what it does without (M5-01).
//
// Separated from the session manager because feature negotiation is the part that is *decided*
// and the session is the part that is *performed*. A required feature a headset does not have
// is a session that never starts, so the split between required and optional here is the whole
// difference between "MR is unavailable on this device" and "MR runs without plane detection".
//
// **Nothing that can degrade is required.** The rule this file encodes: a feature goes in
// `required` only when the mode is meaningless without it. `local-floor` is required for VR
// because a machine standing at the wrong height is not a pinball cabinet, and `hit-test` is
// required for MR because there is nowhere to put the machine without it. Hand tracking is
// *optional* in both, because a player with controllers has a complete set of controls (06 §2.1)
// — asking for it as required would refuse the session outright on a runtime with hands turned
// off in system settings, which is a setting, not a capability.

/** The two immersive modes this product offers. `inline` is the windowed view and is not XR. */
export type ImmersiveMode = 'immersive-vr' | 'immersive-ar'

/** What one mode asks for, in the shape `requestSession` takes. */
export interface SessionFeatures {
  readonly requiredFeatures: readonly string[]
  readonly optionalFeatures: readonly string[]
}

/**
 * The VR arcade's features.
 *
 * `local-floor` required: the venue is a room with a floor and the cabinet's lockdown bar has to
 * land at the player's hip (06 §3), which cannot be done from a `local` space whose origin is
 * wherever the head happened to be.
 *
 * `bounded-floor` optional and *not* required, even though the arcade is a room-scale venue: a
 * player standing at one machine never walks, because smooth locomotion is deliberately not
 * offered and bays are teleported between (06 §4). The guardian bounds are a nicety for drawing
 * the venue to fit, not a requirement for playing in it.
 */
export const VR_FEATURES: SessionFeatures = {
  requiredFeatures: ['local-floor'],
  optionalFeatures: ['bounded-floor', 'hand-tracking', 'layers'],
}

/**
 * The MR cabinet's features.
 *
 * `hit-test` required: the placement ritual is a reticle tracking a real surface, and without a
 * hit test there is no surface and no ritual — the machine would have to be dropped at a guessed
 * distance in front of the player, which is the failure mode 06 §3 exists to avoid.
 *
 * `anchors` optional rather than required, because the fallback is specified and good: an anchor
 * that cannot be created or re-localised sends the player back to safe placement (06 §3), which
 * is one pinch. Requiring anchors would turn a one-pinch inconvenience into "MR is unavailable".
 */
export const AR_FEATURES: SessionFeatures = {
  requiredFeatures: ['local-floor', 'hit-test'],
  optionalFeatures: ['anchors', 'plane-detection', 'hand-tracking', 'layers', 'light-estimation'],
}

/** What a mode asks for. */
export function featuresFor(mode: ImmersiveMode): SessionFeatures {
  return mode === 'immersive-ar' ? AR_FEATURES : VR_FEATURES
}

/**
 * Which optional features the runtime actually granted.
 *
 * WebXR has no direct "what did I get" call for most features, so this reads the session's
 * `enabledFeatures` where the runtime reports it and falls back to assuming nothing optional was
 * granted. **Assuming nothing is the safe direction**: a session that believes it has anchors
 * and does not will fail at the moment the player places the machine, which is the worst moment
 * available. A session that believes it has none and does simply re-places more often.
 */
export function grantedFeatures(session: { enabledFeatures?: readonly string[] }): Set<string> {
  const listed = session.enabledFeatures
  return new Set(listed === undefined ? [] : listed)
}
